import { EventSourceMessage, fetchEventSource } from '@microsoft/fetch-event-source';

import config from '../../../config';
import { BaseRequestService } from '../BaseRequest.service';
import { ICloseoutService } from '../types';

import { AccountCloseoutResponse, CloudEvent } from './types';

// Declare global types (available in modern browsers and fetch polyfills)
interface AbortController {
  readonly signal: AbortSignal;
  abort(): void;
}

interface AbortSignal extends EventTarget {
  readonly aborted: boolean;
}

declare const AbortController: {
  new(): AbortController;
};

interface Response {
  readonly ok: boolean;
  readonly status: number;
  readonly statusText: string;
}

const API_PATHS = {
  accountCloseoutStatus: '/v1/account/status/$1',
  accountEvents: '/v1/account/events/$1',
};

export class CloseoutService extends BaseRequestService implements ICloseoutService {
  protected getBaseUrl(): string {
    return config.closeout.url;
  }

  public getAccountCloseoutStatus(accountReferenceId: string) {
    let url = API_PATHS.accountCloseoutStatus.replace('$1', accountReferenceId);
    return this.api.get<AccountCloseoutResponse>(url);
  }

  public streamAccountEvents(
    accountRefId: string,
    onEvent: (event: CloudEvent) => void,
    onError?: (error: Error) => void,
    onComplete?: () => void,
  ): () => void {
    const url = `${this.getBaseUrl()}${API_PATHS.accountEvents.replace('$1', accountRefId)}`;

    const abortController = new AbortController();

    // Guard so onComplete fires exactly once regardless of which path closes the stream.
    let closed = false;
    const notifyClose = () => {
      if (closed) return;
      closed = true;
      onComplete?.();
    };

    try {
      fetchEventSource(url, {
        headers: {
          'X-CSRF': '1',
        },
        credentials: 'include',
        signal: abortController.signal,
        openWhenHidden: true,
        async onopen(response: Response) {
          if (response.ok) {
            return; // Success
          }

          const errorMessage = `HTTP ${response.status}: ${response.statusText}`;
          console.error('[CloseoutService] SSE connection failed:', errorMessage);
          throw new Error(errorMessage);
        },
        onmessage(event: EventSourceMessage) {
          try {
            // Handle potential SSE protocol prefix (defensive)
            let jsonData = event.data;

            // Check if data starts with "data: " prefix (malformed SSE)
            if (jsonData.startsWith('data:')) {
              jsonData = jsonData.substring(5).trim();
            }

            // Skip empty messages
            if (!jsonData || jsonData.trim().length === 0) {
              return;
            }

            // Skip heartbeat/keep-alive messages (timestamp-only, not JSON)
            if (!jsonData.startsWith('{') && !jsonData.startsWith('[')) {
              return;
            }

            const data: CloudEvent = JSON.parse(jsonData);
            onEvent(data);
          } catch (error) {
            console.error('[CloseoutService] Error parsing SSE message:', error);
            onError?.(error as Error);
          }
        },
        onerror(error: any) {
          onError?.(error as Error);
          throw error; // Stops the connection
        },
        onclose() {
          notifyClose();
        },
      } as any).catch((err: Error) => {
        onError?.(err);
      });
    } catch (error) {
      console.error('[CloseoutService] Failed to initiate SSE connection:', error);
      onError?.(error as Error);
    }

    // Return cleanup function
    return () => {
      abortController.abort();
      notifyClose();
    };
  }
}
